import { useEffect, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { client, type AppRequest, type ServiceStatus } from '../lib/api';
import { RequestStatusChip, ServiceStatusChip } from '../components/StatusChip';
import { useAuth } from '../lib/AuthContext';
import { PageHeader, Section } from '../components/Page';

type AdminUser = {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  department: string;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
};

const REQUEST_STATUSES = ['submitted', 'reviewing', 'scheduled', 'in_progress', 'delivered', 'declined'];
const SERVICE_STATUSES = ['operational', 'degraded', 'outage', 'maintenance'];

export function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [requests, setRequests] = useState<AppRequest[]>([]);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'member'>('member');
  const [newDept, setNewDept] = useState('');

  async function reload() {
    const [u, r, s] = await Promise.all([client.users(), client.requests(), client.status()]);
    setUsers(u.users);
    setRequests(r.requests);
    setServices(s.services);
  }

  useEffect(() => {
    if (user?.role !== 'admin') return;
    reload().catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [user?.role]);

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  async function createUser(e: FormEvent) {
    e.preventDefault();
    setError('');
    setInfo('');
    try {
      const res = await client.createUser({
        email: newEmail,
        name: newName,
        role: newRole,
        department: newDept,
      });
      setInfo(`Created ${res.user.email}. Temporary password: ${res.temporaryPassword}`);
      setNewEmail('');
      setNewName('');
      setNewDept('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  return (
    <>
    <PageHeader title="Admin" subtitle="Manage users, app requests and appointments, and service status." />
    <Section py={{ xs: 5, md: 7 }}>
    <Stack spacing={3}>

      {error && <Alert severity="error">{error}</Alert>}
      {info && <Alert severity="info">{info}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Users" />
        <Tab label="Requests" />
        <Tab label="Status" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Add user
              </Typography>
              <Stack component="form" spacing={1.5} onSubmit={createUser}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} required fullWidth />
                  <TextField label="Email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField select label="Role" value={newRole} onChange={(e) => setNewRole(e.target.value as 'admin' | 'member')} sx={{ minWidth: 160 }}>
                    <MenuItem value="member">Member</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </TextField>
                  <TextField label="Department" value={newDept} onChange={(e) => setNewDept(e.target.value)} fullWidth />
                  <Button type="submit" variant="contained">
                    Create
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Directory
              </Typography>
              <Stack spacing={2} divider={<Divider flexItem />}>
                {users.map((u) => (
                  <Stack key={u.id} direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography variant="subtitle2">
                        {u.name} · {u.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.role} · {u.department || '—'} · {u.active ? 'active' : 'inactive'}
                        {u.mustChangePassword ? ' · must change password' : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        size="small"
                        onClick={async () => {
                          await client.updateUser(u.id, { active: !u.active });
                          await reload();
                        }}
                      >
                        {u.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="small"
                        onClick={async () => {
                          const res = await client.resetPassword(u.id);
                          setInfo(`Temp password for ${u.email}: ${res.temporaryPassword}`);
                        }}
                      >
                        Reset password
                      </Button>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {tab === 1 && (
        <Stack spacing={2}>
          {requests.map((r) => (
            <Card key={r.id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" gap={2} mb={1}>
                  <Box>
                    <Typography variant="subtitle1">{r.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {r.requester_name} ({r.requester_email}) · {r.department}
                    </Typography>
                  </Box>
                  <RequestStatusChip status={r.status} />
                </Stack>
                <Typography variant="body2" mb={2}>
                  {r.description}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    label="Status"
                    size="small"
                    value={r.status}
                    onChange={async (e) => {
                      await client.updateRequest(r.id, { status: e.target.value });
                      await reload();
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    {REQUEST_STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s.replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Appointment"
                    type="datetime-local"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    defaultValue={r.appointment_at ? r.appointment_at.slice(0, 16) : ''}
                    onBlur={async (e) => {
                      const val = e.target.value ? new Date(e.target.value).toISOString() : null;
                      await client.updateRequest(r.id, { appointmentAt: val, status: val ? 'scheduled' : r.status });
                      await reload();
                    }}
                  />
                  <TextField
                    label="Admin notes"
                    size="small"
                    fullWidth
                    defaultValue={r.admin_notes}
                    onBlur={async (e) => {
                      if (e.target.value === r.admin_notes) return;
                      await client.updateRequest(r.id, { adminNotes: e.target.value });
                      await reload();
                    }}
                  />
                </Stack>
              </CardContent>
            </Card>
          ))}
          {!requests.length && <Alert severity="info">No custom app requests yet.</Alert>}
        </Stack>
      )}

      {tab === 2 && (
        <Stack spacing={2}>
          {services.map((s) => (
            <Card key={s.id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={1.5}>
                  <Typography variant="subtitle1">{s.name}</Typography>
                  <ServiceStatusChip status={s.status} />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    select
                    label="Status"
                    size="small"
                    value={s.status}
                    onChange={async (e) => {
                      await client.updateService(s.id, { status: e.target.value, message: s.message });
                      await reload();
                    }}
                    sx={{ minWidth: 180 }}
                  >
                    {SERVICE_STATUSES.map((st) => (
                      <MenuItem key={st} value={st}>
                        {st}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Message"
                    size="small"
                    fullWidth
                    defaultValue={s.message}
                    onBlur={async (e) => {
                      if (e.target.value === s.message) return;
                      await client.updateService(s.id, { status: s.status, message: e.target.value });
                      await reload();
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      const title = window.prompt('Incident title');
                      if (!title) return;
                      const body = window.prompt('Details (optional)') ?? '';
                      await client.addIncident(s.id, { title, body, severity: 'minor' });
                      setInfo(`Incident logged on ${s.name}`);
                    }}
                  >
                    Log incident
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
    </Section>
    </>
  );
}
